import { useLocation, useNavigate } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAuthStore } from '../../lib/auth-store';
import { useToggleWishlist, useWishlist } from '../lib/hooks';

/**
 * Save / un-save a product (a course IS a product, so one control serves both
 * catalogs). The write is optimistic in the hook, so the fill flips instantly.
 *
 * It is mounted INSIDE `<Link>` cards, so every activation stops the event
 * dead — otherwise saving a course would also navigate away from the grid the
 * learner is browsing.
 */
export function WishlistHeart({
  productId,
  variant = 'icon',
  className = '',
  /** Names the thing being saved in the a11y label ("Save this course"). */
  noun = 'item',
}: {
  productId: string;
  variant?: 'icon' | 'button';
  className?: string;
  noun?: string;
}) {
  const authed = !!useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const location = useLocation();
  const { ids } = useWishlist();
  const toggle = useToggleWishlist();
  const wished = ids.has(productId);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Saving needs an account. Come back to exactly where they were, search
    // string included, so a filtered grid survives the round trip.
    if (!authed) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    toggle.mutate({ productId, wished });
  };

  const label = wished ? `Remove this ${noun} from saved` : `Save this ${noun}`;

  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={wished}
        aria-label={label}
        className={`flex w-full items-center justify-center gap-2 rounded border border-[#d1d7dc] py-2.5 text-sm font-bold text-[#222] transition-colors hover:bg-[#f7f9fa] ${className}`}
      >
        <Heart
          className="h-4 w-4"
          fill={wished ? 'hsl(var(--primary))' : 'none'}
          color={wished ? 'hsl(var(--primary))' : 'currentColor'}
        />
        {wished ? 'Saved' : 'Save'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={wished}
      aria-label={label}
      className={`flex h-[34px] w-[34px] items-center justify-center rounded-full bg-white shadow-md ${className}`}
    >
      <Heart
        className="h-4 w-4"
        fill={wished ? 'hsl(var(--primary))' : 'none'}
        color={wished ? 'hsl(var(--primary))' : '#888'}
      />
    </button>
  );
}
